import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

const MONEY_FORMATTER = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PERCENTAGE_FORMATTER = new Intl.NumberFormat("es-AR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function CategorySummaryTable({ title, categories, total, tone, onSelectCategory }) {
  const isIncome = tone === "income";
  const accent = isIncome ? "#137c46" : "#b71c1c";
  const headerBg = isIncome ? "rgba(88, 214, 128, 0.72)" : "rgba(255, 112, 112, 0.72)";
  const bodyBg = isIncome ? "rgba(190, 246, 202, 0.62)" : "rgba(255, 205, 205, 0.68)";
  const movementCount = categories.reduce(
    (sum, category) => sum + Number(category.movementCount || 0),
    0
  );

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: `2px solid ${accent}`,
        borderRadius: "14px",
        bgcolor: bodyBg,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Typography sx={{ px: 1.5, pt: 1.2, fontSize: 14, fontWeight: 900, color: accent }}>
        {title}
      </Typography>
      <Table size="small" aria-label={title} sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow sx={{ bgcolor: headerBg }}>
            {[
              ["Categoría", "left", "44%"],
              ["Movimientos", "right", "18%"],
              ["Total", "right", "22%"],
              ["participation", "right", "16%"],
            ].map(([label, align, width]) => (
              <TableCell
                key={label}
                align={align}
                sx={{ width, py: 0.8, fontWeight: 900, color: "primary.900" }}
              >
                {label === "participation" ? (
                  <>
                    <Box component="span" sx={{ display: { xs: "none", xl: "inline" } }}>
                      Participación
                    </Box>
                    <Box component="span" sx={{ display: { xs: "inline", xl: "none" } }}>
                      %
                    </Box>
                  </>
                ) : (
                  label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {categories.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 3, fontWeight: 700, color: "neutral.600" }}>
                No hay movimientos confirmados para este tipo.
              </TableCell>
            </TableRow>
          ) : (
            categories.map((category) => (
              <TableRow key={category.categoryId ?? "uncategorized"} hover>
                <TableCell sx={{ py: 0.6 }}>
                  <Button
                    variant="text"
                    onClick={() => onSelectCategory(category)}
                    sx={{
                      p: 0,
                      minWidth: 0,
                      width: "100%",
                      justifyContent: "flex-start",
                      textAlign: "left",
                      color: "primary.900",
                      textTransform: "none",
                      fontWeight: 800,
                      lineHeight: 1.2,
                    }}
                  >
                    {category.categoryName}
                  </Button>
                </TableCell>
                <TableCell align="right">{category.movementCount}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, color: accent }}>
                  {MONEY_FORMATTER.format(Number(category.amount || 0))}
                </TableCell>
                <TableCell align="right">
                  {PERCENTAGE_FORMATTER.format(Number(category.percentage || 0))} %
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <Box sx={{ flex: 1, minHeight: 0 }} />
      <Box
        role="row"
        sx={{
          display: "grid",
          gridTemplateColumns: "44% 18% 22% 16%",
          alignItems: "center",
          bgcolor: headerBg,
          minHeight: 34,
          "& > *": { px: 2, py: 0.75, fontSize: 14 },
        }}
      >
        <Typography role="cell" sx={{ fontSize: 14, fontWeight: 900, lineHeight: 1.25 }}>
          Total
        </Typography>
        <Typography
          role="cell"
          align="right"
          sx={{ fontSize: 14, fontWeight: 900, lineHeight: 1.25 }}
        >
          {movementCount}
        </Typography>
        <Typography
          role="cell"
          align="right"
          sx={{ color: accent, fontSize: 14, fontWeight: 900, lineHeight: 1.25 }}
        >
          {MONEY_FORMATTER.format(Number(total || 0))}
        </Typography>
        <Typography
          role="cell"
          align="right"
          sx={{ fontSize: 14, fontWeight: 900, lineHeight: 1.25 }}
        >
          {categories.length > 0 ? "100 %" : "0 %"}
        </Typography>
      </Box>
    </TableContainer>
  );
}

export default function CierreResumen({
  report,
  loading,
  error,
  navigation,
  onSelectCategory,
  statusLabel = "Modo lectura",
}) {
  const incomeCategories = report?.summary?.incomeCategories || [];
  const expenseCategories = report?.summary?.expenseCategories || [];

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: "20px",
        bgcolor: "rgba(255,255,255,0.86)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        minHeight: 0,
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems={{ xs: "stretch", md: "center" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{navigation}</Box>
        <Chip
          icon={<VisibilityIcon sx={{ fontSize: 17 }} />}
          label={statusLabel}
          size="small"
          sx={{ alignSelf: { xs: "flex-start", md: "center" }, bgcolor: "rgba(185, 246, 202, 0.72)", fontWeight: 900 }}
        />
      </Stack>

      {loading ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
          <CircularProgress size={30} />
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Cargando resumen...</Typography>
        </Stack>
      ) : null}
      {!loading && error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      {!loading && !error ? (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            <CategorySummaryTable
              title="Ingresos por categoría"
              categories={incomeCategories}
              total={report?.summary?.totalIncome}
              tone="income"
              onSelectCategory={(category) => onSelectCategory("income", category)}
            />
            <CategorySummaryTable
              title="Egresos por categoría"
              categories={expenseCategories}
              total={report?.summary?.totalExpense}
              tone="expense"
              onSelectCategory={(category) => onSelectCategory("expense", category)}
            />
          </Box>
        </Stack>
      ) : null}
    </Paper>
  );
}
